export interface CallBackData {
  custom_fields: {
    user_id: string;
    instruction_mode?: 'add' | 'update';
    anchor_key?: 'auto' | 'url' | 'sku';
  };
  destination_type: string;
  import_id: number;
  import_status: string;
  raw_file: string;
  row_count: number;
  row_fail: number;
  row_success: number;
  sheet_id: number;
  sheet_name: string;
  original_filename: string;
  ssv_row_fail: number;
  external_validations_failed_requests: number;
  ssv_table_error: number;
  if_table_error: number;
  ssv_fail: number;
  ev_failed: number;
  ssv_enabled: number;
  external_validation: number;
  import_starttime: number;
  import_endtime: number;
  env_name: string;
  import_description?: string;
  column_mappings: Array<Record<string, string>>;
  /** Present when "Send JSON data to client" is enabled in CSVBox sheet Destination. Avoids CORS (no fetch of raw_file). */
  rows?: Record<string, unknown>[];
  raw_columns?: string[];
  ignored_columns?: string[];
}

type Callback = (result: boolean, data: CallBackData) => void;

const CSV_BOX_PHYSICAL_PRODUCT_TEMPLATE = 'CKUJY4cwdL5wp1UNIohKL8XhBi78sH';

declare global {
  class CSVBoxImporter {
    public readonly setOptions: (options: Record<string, unknown>) => void;
    public readonly setUser: (user: {
      user_id: string;
      instruction_mode: string;
      anchor_key?: string;
    }) => void;
    public readonly openModal: () => void;

    constructor(
      apiKey: string,
      options: Record<string, unknown>,
      callback: (result: boolean, data: CallBackData) => void
    );
  }
  interface Window {
    CSVBoxImporter: CSVBoxImporter;
  }
}

export class CsvBox {
  private importer: CSVBoxImporter | null = null;
  private subscribers = new Set<Callback>();
  private readonly importerCallback: Callback = (result, data) => {
    for (const subscriber of this.subscribers) {
      subscriber(result, data);
    }
  };
  constructor() {}

  private ensureImporter() {
    if (!this.importer) {
      this.importer = new CSVBoxImporter(
        CSV_BOX_PHYSICAL_PRODUCT_TEMPLATE,
        {},
        this.importerCallback
      );
    }
  }

  openModal() {
    this.ensureImporter();
    this.importer!.openModal();
  }

  subscribe(callback: Callback) {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
    };
  }

  public setOptions(options: { language: 'fr' | 'en' }) {
    this.ensureImporter();
    this.importer!.setOptions({
      language: options.language,
    });
  }

  public setUser(user: {
    user_id: string;
    instruction_mode: string;
    anchor_key?: string;
  }) {
    this.ensureImporter();
    this.importer!.setUser(user);
  }

  subscribeOnce(callback: Callback) {
    const onceWrapper: Callback = (result, data) => {
      this.subscribers.delete(onceWrapper);
      callback(result, data);
    };
    this.subscribers.add(onceWrapper);
    return () => {
      this.subscribers.delete(onceWrapper);
    };
  }
}

export const csvBoxSingleton = new CsvBox();
